// eslint-disable-next-line no-restricted-imports
import type { MemoizedFunction } from 'lodash';
import memoize from 'lodash/memoize';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import {
	hasSAINLayerParamValue,
	hasSAINHelloLayerParamValue,
	hasSAINUXHelloLayerParamValue,
	hasSAINUXLayerParamValue,
} from './get-sain-layer-frontend-experiment';

const canRenderLuminaUnmemoized = (): boolean => {
	if (fg('kd_dynamic_layout_in_sain')) {
		return true;
	}

	return false;
};

const isSourcesInShortSAINAnswersEnabledUnmemoized = (): boolean => {
	if (fg('kd_sain_show_sources_in_short_answers')) {
		return true;
	}

	return hasSAINLayerParamValue('sourcesInShortSAINAnswers', 'test');
};

const isZeroQuerySuggestionsV1EnabledUnmemoized = (primaryProduct: string | undefined): boolean => {
	if (primaryProduct === 'jira') {
		return fg('kd_jira_prequery_suggestions_v1');
	}
	if (primaryProduct === 'confluence') {
		return fg('kd_confluence_prequery_suggestions_v1');
	}
	return false;
};

const isFullHeightLuminaEnabledUnmemoized = (): boolean => {
	if (fg('kd_enable_full_height_lumina')) {
		return true;
	}
	return false;
};

const isNonPrimaryClickTrackingEnabledUnmemoized = (): boolean => {
	if (fg('kd_track_non_primary_clicks_gate')) {
		return true;
	}

	if (
		FeatureGates.getExperimentValue<string>(
			'kd_track_non_primary_clicks',
			'shouldTrackNonPrimaryClicks',
			'control',
			{ fireExperimentExposure: false },
		) === 'test'
	) {
		return true;
	}

	return false;
};

const triggerNonPrimaryClickTrackingExposure = (): void => {
	try {
		if (!fg('kd_track_non_primary_clicks_gate')) {
			FeatureGates.manuallyLogExperimentExposure('kd_track_non_primary_clicks');
		}
	} catch {
		// Silently fail if FeatureGates is not available (e.g., in test environments)
	}
};

const isNavigationalIntentEnabledUnmemoized = (): boolean => {
	if (fg('kd_sain_enable_navigational_intent_gate')) {
		return true;
	}
	return false;
};

const isInlineCitationsUpdateEnabledUnmemoized = (): boolean => {
	if (fg('kd_team_26_sain_inline_citations_update')) {
		return true;
	}
	if (hasSAINHelloLayerParamValue('InlineCitationUpdate', 'test')) {
		return true;
	}
	if (hasSAINLayerParamValue('InlineCitationUpdate', 'test')) {
		return true;
	}
	return false;
};

const isLinkListSourcesEnabledUnmemoized = (): boolean => {
	if (fg('kd_sain_link_list_sources')) {
		return true;
	}
	if (hasSAINUXLayerParamValue('SAINRecentLinksListEnabled', 'test')) {
		return true;
	}
	return false;
};

const isImprovedChatOptionsEnabledUnmemoized = (): boolean => {
	if (fg('kd_sain_improved_chat_options')) {
		return true;
	}
	if (hasSAINUXLayerParamValue('SAINImprovedChatOptions', 'test')) {
		return true;
	}
	return false;
};

const isSAINUpdatedFlowEnabledUnmemoized = (): boolean => {
	if (fg('kd_sain_user_flow_updates_enabled')) {
		return true;
	}
	if (hasSAINUXHelloLayerParamValue('sainFlowUpdates', 'test')) {
		return true;
	}
	return false;
};

const isPeopleConnectorUXEnabledUnmemoized = (): boolean => {
	if (fg('kd_sain_pt_person_team_26')) {
		return true;
	}
	if (
		hasSAINLayerParamValue('PeopleConnectorUXEnabled', 'uxtreatment') ||
		hasSAINLayerParamValue('PeopleConnectorUXEnabled', 'fulltreatment')
	) {
		return true;
	}
	return false;
};

const isSAINSourceProductUpdateEnabledUnmemoized = (): boolean => {
	if (fg('kd_enable_lumina_list_3p_sources_attributes')) {
		return true;
	}

	if (hasSAINLayerParamValue('SAINSourceProductUpdate', 'test')) {
		return true;
	}

	return false;
};

const isSAINLoadingStateTracesUpdateEnabledUnmemoized = (): boolean => {
	if (fg('kd_enable_sain_loading_state_traces')) {
		return true;
	}
	if (hasSAINUXHelloLayerParamValue('SAINLoadingStateTraces', 'test')) {
		return true;
	}
	if (hasSAINUXLayerParamValue('SAINLoadingStateTraces', 'test')) {
		return true;
	}
	return false;
};

const isSAINScrollUpEnabledUnmemoized = (): boolean => {
	if (fg('kd_enable_sain_scroll_up')) {
		return true;
	}
	if (hasSAINUXHelloLayerParamValue('SAINScrollUp', 'test')) {
		return true;
	}
	if (hasSAINUXLayerParamValue('SAINScrollUp', 'test')) {
		return true;
	}
	return false;
};

const DEFAULT_COLLAB_GRAPH_MODEL = 'cg_user_user_v4_';
type CollabGraphModel = 'cg_user_user_v4_' | 'cg_user_user_v5' | 'cg_user_user_v5_regression';

const getCollabGraphExperimentModelUnmemoized = (): CollabGraphModel => {
	if (fg('cg_sain_user_user_v5')) {
		return 'cg_user_user_v5_regression';
	}

	// Despite the experiment setup not returning an empty string,
	// `get` method can still return an empty string.
	const featureServiceModel = FeatureGates.getExperiment('people-card-collaboration-graph-v5').get(
		'featureServiceModel',
		DEFAULT_COLLAB_GRAPH_MODEL,
	) as CollabGraphModel | '';

	return featureServiceModel === '' ? DEFAULT_COLLAB_GRAPH_MODEL : featureServiceModel;
};

export const SAINFeatureControl: {
	/** Controls whether frontend can parse and render lumina-formatted answers */
	canRenderLumina: (() => boolean) & MemoizedFunction;
	isFullHeightLuminaEnabled: (() => boolean) & MemoizedFunction;
	isSourcesInShortSAINAnswersEnabled: (() => boolean) & MemoizedFunction;
	isZeroQuerySuggestionsV1Enabled: ((primaryProduct: string | undefined) => boolean) &
		MemoizedFunction;
	isNonPrimaryClickTrackingEnabled: (() => boolean) & MemoizedFunction;
	triggerNonPrimaryClickTrackingExposure: () => void;
	isNavigationalIntentEnabled: (() => boolean) & MemoizedFunction;
	isInlineCitationsUpdateEnabled: (() => boolean) & MemoizedFunction;
	isLinkListSourcesEnabled: (() => boolean) & MemoizedFunction;
	isPeopleConnectorUXEnabled: (() => boolean) & MemoizedFunction;
	isSAINUpdatedFlowEnabled: (() => boolean) & MemoizedFunction;
	isImprovedChatOptionsEnabled: (() => boolean) & MemoizedFunction;
	isSAINSourceProductUpdateEnabled: (() => boolean) & MemoizedFunction;
	getCollabGraphExperimentModel: (() => CollabGraphModel) & MemoizedFunction;
	isSAINLoadingStateTracesUpdateEnabled: (() => boolean) & MemoizedFunction;
	isSAINScrollUpEnabled: (() => boolean) & MemoizedFunction;
} = {
	canRenderLumina: memoize(canRenderLuminaUnmemoized),
	isFullHeightLuminaEnabled: memoize(isFullHeightLuminaEnabledUnmemoized),
	isSourcesInShortSAINAnswersEnabled: memoize(isSourcesInShortSAINAnswersEnabledUnmemoized),
	isZeroQuerySuggestionsV1Enabled: memoize(isZeroQuerySuggestionsV1EnabledUnmemoized),
	isNonPrimaryClickTrackingEnabled: memoize(isNonPrimaryClickTrackingEnabledUnmemoized),
	triggerNonPrimaryClickTrackingExposure: triggerNonPrimaryClickTrackingExposure,
	isNavigationalIntentEnabled: memoize(isNavigationalIntentEnabledUnmemoized),
	isInlineCitationsUpdateEnabled: memoize(isInlineCitationsUpdateEnabledUnmemoized),
	isLinkListSourcesEnabled: memoize(isLinkListSourcesEnabledUnmemoized),
	isPeopleConnectorUXEnabled: memoize(isPeopleConnectorUXEnabledUnmemoized),
	isSAINUpdatedFlowEnabled: memoize(isSAINUpdatedFlowEnabledUnmemoized),
	isImprovedChatOptionsEnabled: memoize(isImprovedChatOptionsEnabledUnmemoized),
	isSAINSourceProductUpdateEnabled: memoize(isSAINSourceProductUpdateEnabledUnmemoized),
	getCollabGraphExperimentModel: memoize(getCollabGraphExperimentModelUnmemoized),
	isSAINLoadingStateTracesUpdateEnabled: memoize(isSAINLoadingStateTracesUpdateEnabledUnmemoized),
	isSAINScrollUpEnabled: memoize(isSAINScrollUpEnabledUnmemoized),
};
